import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "../styles/OrderEntry.css";
import dayjs, { Dayjs } from "dayjs";
import {
  fetchCustomers,
  searchCustomers,
  fetchCustomerByCodes as fetchCustomerByMtCodeAndBranchCode,
} from "../../../entities/customer/api/customersApi";
import {
  fetchUniqueProducts,
  searchProducts,
} from "../../../entities/product/api/productsApi";
import { fetchLinesByCustomer as fetchLineByCustomer } from "../../customer-management/api/customerManagementApi";
import { fetchShodanOrderDetail } from "../../../entities/shodan-order/api/shodanOrdersApi";
import {
  createOrder,
  fetchOrder,
  updateOrder,
  validateOrderLeadTime,
} from "../api/ordersApi";
import type {
  OrderLeadTimeValidationItem,
  OrderItemEditor as OrderItem,
  OrderType,
} from "../model/types";
import { findDuplicateOrderItemRows } from "../model/itemValidation";
import { getStaffDisplayName } from "../model/staffDisplay";
import {
  buildLeadTimeErrorMessage,
  getLeadTimeResult,
  indexLeadTimeResults,
} from "../model/leadTimeValidation";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import BasicInfoCard from "../components/BasicInfoCard";
import OrderItemCard from "../components/OrderItemCard";
import { PlusOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { appNotification } from "../../../shared/notifications/appNotification";
import { formatYen } from "../../../shared/utils/price";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

export type SelectOption = {
  value: number | string;
  label: string;
};

type StaffOption = {
  value: number | string;
  label: string;
  line: any;
};

const customerToOption = (customer: any): SelectOption => {
  const mtCode = (customer.mt_code || customer.customer_code || "").trim();
  const branchCode = (customer.branch_code || "").trim();
  return {
    value: `${mtCode}::${branchCode}`,
    label: branchCode ? `${mtCode} - ${branchCode}` : mtCode,
  };
};

const OrderEntrySM = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
  const [customerSelected, setCustomerSeleted] =
    useState<SelectOption | null>();
  const [customerLoading, setCustomerLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [productSelected, setProductSeleted] = useState<SelectOption | null>(
    null,
  );
  const [productLoading, setProductLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<number | string>();
  const [salesDate, setSalesDate] = useState<Dayjs | null>(null);
  const [companyData, setCompanyData] = useState<any>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [orderNotes, setOrderNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [leadTimeResults, setLeadTimeResults] = useState<
    Record<string, OrderLeadTimeValidationItem>
  >({});
  const [leadTimeChecking, setLeadTimeChecking] = useState(false);
  const [leadTimeCheckError, setLeadTimeCheckError] = useState<string | null>(
    null,
  );

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leadTimeRequestRef = useRef(0);
  const isInitialMount = useRef(true);

  const orderId = useMemo(() => {
    const idParam = searchParams.get("id");
    return idParam ? Number(idParam) : null;
  }, [searchParams]);

  const orderType = useMemo<OrderType | "">(() => {
    const requestedType = searchParams.get("type");
    return requestedType === "SINGLE" || requestedType === "MULTIPLE"
      ? requestedType
      : "";
  }, [searchParams]);

  const mode = useMemo(() => {
    const path = location.pathname;

    if (path.includes("view")) return "VIEW";
    if (path.includes("create-products")) return "CREATE_MULTI";
    if (path.includes("create-product")) return "CREATE_SINGLE";

    return searchParams.get("copy") === "true" ? "COPY" : "EDIT";
  }, [location.pathname, searchParams]);

  const sourceOrder = useMemo(() => {
    return searchParams.get("source") || "order";
  }, [searchParams]);

  const isSingle =
    (orderType === "SINGLE" || mode === "CREATE_SINGLE") &&
    sourceOrder !== "shodan";
  const currentOrderType: OrderType = isSingle ? "SINGLE" : "MULTIPLE";
  const isReadOnly = mode === "VIEW";
  const leadTimeProductCodes = useMemo(() => {
    const codes = isSingle
      ? productSelected?.value
        ? [String(productSelected.value)]
        : []
      : items
          .map((item) => item.product_code?.trim())
          .filter((code): code is string => Boolean(code));
    return Array.from(new Set(codes));
  }, [isSingle, productSelected?.value, items]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        if (orderId) {
          // VIEW mode: skip fetching dropdown options (customers/products lists)
          // only fetch them when in editable modes
          if (!isReadOnly) {
            await loadCustomersAndProducts();
          }

          const order = await fetchOrder(orderId);
          setOrderNotes(order.notes || "");

          if (!isReadOnly) {
            const productOptionsOrder = order.order_items?.map((item: any) => ({
              value: item.product_code.trim(),
              label: `${item.product_code} - ${item.product_name}`,
            }));
            setProductOptions((prev) => [
              ...prev,
              ...(productOptionsOrder || []),
            ]);
          }

          if (mode === "COPY") {
            setSalesDate(null);
          } else {
            setSalesDate(order.sales_date ? dayjs(order.sales_date) : null);
          }

          if (order.customer_id) {
            const customerOption = {
              value: order.customer_id,
              label:
                order.branch_code && order.branch_code.trim() !== ""
                  ? `${order.customer_code} - ${order.branch_code}`
                  : order.customer_code || "",
            };

            setCustomerSeleted(customerOption);

            // Only load staff dropdown in editable modes
            if (!isReadOnly) {
              await loadStaffsData(customerOption);

              setCustomerOptions((prev) => {
                const exists = prev.some((x) => x.value === order.customer_id);
                if (!exists) return [...prev, customerOption];
                return prev;
              });
            }

            if (order.line_id) {
              const staffValue = order.group_id
                ? `${order.group_id}::${order.line_id}`
                : order.line_id;
              setSelectedStaffId(staffValue);
              setStaffOptions((previous) => {
                if (previous.some((option) => option.value === staffValue)) {
                  return previous;
                }
                return [
                  ...previous,
                  {
                    value: staffValue,
                    label: order.staff_name || "-",
                    line: {
                      group_id: order.group_id,
                      line_id: order.line_id,
                      staff_name: order.staff_name,
                    },
                  },
                ];
              });
            }
          }

          const lines = order.order_items || [];
          if (lines.length > 0) {
            if (isSingle && lines[0].product_code) {
              setProductSeleted({
                value: lines[0].product_code.trim(),
                label: `${lines[0].product_code} - ${lines[0].product_name}`,
              });
            }

            const initialItems = lines.map((line: any, index: number) => {
              const newLine = {
                ...line,
                id: line.id || Date.now() + index,
                product_code: line.product_code?.trim(),
                order_quantity: Number(line.order_quantity || 0),
                proposed_price: Number(line.proposed_price || 0),
                line_total: Number(line.line_total || 0),
              };
              if (mode === "COPY") {
                newLine.id = Date.now() + index;
              }
              return newLine;
            });
            setItems(initialItems);
          }
        } else if (sourceOrder === "shodan") {
          setOrderNotes("");
          const customerCode = searchParams.get("customer_code");
          const branchCode = searchParams.get("branch_code");
          const orderNum = searchParams.get("order_number");
          const deliveryDate = searchParams.get("delivery_date");

          if (
            !customerCode ||
            branchCode === null ||
            !orderNum ||
            !deliveryDate
          ) {
            throw new Error(
              "SHODAN order detail requires customer, branch, order number, and delivery date.",
            );
          }

          const shodanItems = await fetchShodanOrderDetail(
            customerCode,
            branchCode,
            orderNum,
            deliveryDate,
          );
          if (shodanItems.length > 0) {
            const firstItem = shodanItems[0];
            setSalesDate(
              mode === "COPY"
                ? null
                : firstItem.delivery_date
                  ? dayjs(firstItem.delivery_date)
                  : null,
            );

            const customerOption = {
              value: customerCode,
              label:
                branchCode && branchCode.trim() !== ""
                  ? `${customerCode} - ${branchCode}`
                  : customerCode,
            };
            setCustomerSeleted(customerOption);

            // Only load staff/customer dropdowns in editable modes
            if (!isReadOnly) {
              await loadStaffsData(customerOption);
              setCustomerOptions((prev) => {
                const exists = prev.some(
                  (x) => x.label === customerOption.label,
                );
                if (!exists) return [...prev, customerOption];
                return prev;
              });

              const productOpts = shodanItems.map((item: any) => ({
                value: item.product_code?.trim(),
                label: `${item.product_code} - ${item.product_name}`,
              }));
              setProductOptions((prev) => [...prev, ...productOpts]);
            }

            const mappedItems = shodanItems.map((line: any, index: number) => ({
              id: Date.now() + index,
              product_code: line.product_code?.trim(),
              product_name: line.product_name,
              order_quantity: Number(line.order_qty || 0),
              proposed_price: Number(line.proposal_price || 0),
              line_total:
                Number(line.order_qty || 0) * Number(line.proposal_price || 0),
              origin_name: line.production,
              shipper_name: line.shipper,
              grade: line.grade,
              rank: line.class,
              weight: line.size,
              package_type: line.packaging,
              quantity_per_package: Number(line.quantity || 0),
              notes: line.note,
              daily_note: line.daily_note,
              weekly_note: line.weekly_note,
              order_type: "MULTIPLE" as const,
            }));
            setItems(mappedItems);
          }
        } else {
          setOrderNotes("");
          // INITIALIZE NEW ORDER
          await loadCustomersAndProducts();
          if (!isSingle) {
            setItems([
              {
                id: Date.now(),
                order_quantity: 1,
                order_type: currentOrderType,
              },
            ]);
          } else {
            setItems([]);
          }
        }
      } catch (error: unknown) {
        const apiError = normalizeApiError(error);
        appNotification.error({
          message: "注文データ取得エラー",
          description:
            apiError.message ||
            "注文データを取得できませんでした。注文番号と関連データを確認してください。",
          duration: 8,
        });
      } finally {
        setLoading(false);
        isInitialMount.current = false;
      }
    };
    init();
  }, [
    orderId,
    orderType,
    isSingle,
    currentOrderType,
    mode,
    sourceOrder,
    isReadOnly,
    searchParams,
  ]);

  useEffect(() => {
    if (loading || isReadOnly) return;

    const customerParts = customerSelected?.label?.split(" - ") || [];
    const customerCode = customerParts[0]?.trim() || "";
    const branchCode = customerParts[1]?.trim() || "";
    const shouldCheck = Boolean(
      customerCode &&
      leadTimeProductCodes.length > 0 &&
      (isSingle || salesDate),
    );
    const requestId = ++leadTimeRequestRef.current;

    if (!shouldCheck) {
      setLeadTimeResults({});
      setLeadTimeCheckError(null);
      setLeadTimeChecking(false);
      return;
    }

    setLeadTimeChecking(true);
    setLeadTimeCheckError(null);
    void validateOrderLeadTime({
      customer_code: customerCode,
      branch_code: branchCode,
      product_codes: leadTimeProductCodes,
      delivery_date: salesDate?.format("YYYY-MM-DD") || null,
    })
      .then((response) => {
        if (requestId !== leadTimeRequestRef.current) return;
        setLeadTimeResults(indexLeadTimeResults(response.items));
      })
      .catch((error: unknown) => {
        if (requestId !== leadTimeRequestRef.current) return;
        const apiError = normalizeApiError(error);
        setLeadTimeResults({});
        setLeadTimeCheckError(
          apiError.message ||
            "注文期限を確認できませんでした。保存時に再確認します。",
        );
      })
      .finally(() => {
        if (requestId === leadTimeRequestRef.current) {
          setLeadTimeChecking(false);
        }
      });
  }, [
    customerSelected?.label,
    isReadOnly,
    isSingle,
    leadTimeProductCodes,
    loading,
    salesDate,
  ]);

  // Hàm lấy thông tin tên công ty dựa trên khách hàng đã chọn
  const updateCompanyName = useCallback(
    async (option: SelectOption | null | undefined) => {
      if (!option) {
        setCompanyData(null);
        return;
      }
      const label = option.label || "";
      const parts = label.split(" - ");
      const mtCode = parts[0]?.trim();
      const branchCode = parts[1]?.trim();

      if (mtCode) {
        try {
          const customerData = await fetchCustomerByMtCodeAndBranchCode(
            mtCode,
            branchCode,
          );
          setCompanyData(customerData);
        } catch (err) {
          console.error(
            "Failed to fetch customer detail for company name:",
            err,
          );
          setCompanyData(null);
        }
      }
    },
    [],
  );

  // Cập nhật tên công ty mỗi khi customerSelected thay đổi (bao gồm cả lúc init form edit/copy)
  // VIEW mode: skip both company name fetch and staff list fetch
  useEffect(() => {
    if (isReadOnly) {
      updateCompanyName(customerSelected);
      return;
    }
    updateCompanyName(customerSelected);
    loadStaffsData(customerSelected || null);
  }, [customerSelected, updateCompanyName, isReadOnly]);

  // Logic đồng bộ sản phẩm từ Header xuống các dòng khi người dùng THAY ĐỔI sản phẩm (chỉ dùng cho Single Mode)
  useEffect(() => {
    if (loading || isInitialMount.current) return;

    if (isSingle) {
      if (productSelected?.value) {
        setItems((currentItems) => {
          const pCode = productSelected.value as string;
          const pName =
            productSelected.label?.split(" - ")[1] || productSelected.label;

          if (currentItems.length === 0) {
            return [
              {
                id: Date.now(),
                order_quantity: 5,
                order_type: currentOrderType,
                product_code: pCode,
                product_name: pName,
              },
            ];
          }

          if (
            currentItems.length > 0 &&
            currentItems[0].product_code !== pCode
          ) {
            return currentItems.map((item) => ({
              ...item,
              product_code: pCode,
              product_name: pName,
              origin_id: undefined,
              origin_name: undefined,
              origin_code: undefined,
              shipper_id: undefined,
              shipper_name: undefined,
              shipper_code: undefined,
              grade: undefined,
              rank: undefined,
              order_unit: undefined,
              weight: undefined,
              package_type: undefined,
              quantity_per_package: undefined,
            }));
          }

          return currentItems;
        });
      } else {
        setItems((currentItems) => {
          if (currentItems.length > 0) {
            return [];
          }
          return currentItems;
        });
      }
    }
  }, [
    productSelected?.value,
    productSelected?.label,
    loading,
    isSingle,
    currentOrderType,
  ]);

  const loadStaffsData = async (customer: SelectOption | null) => {
    if (!customer) {
      setStaffOptions([]);
      return;
    }

    try {
      const parts = customer.label.split(" - ");

      const mtCode = parts[0]?.trim();
      const branchCode = parts[1]?.trim();

      const lines = await fetchLineByCustomer(mtCode, branchCode);

      setStaffOptions(
        lines.map((line: any) => ({
          value: line.id,
          label: getStaffDisplayName(line),
          line,
        })),
      );
    } catch (err) {
      console.error(err);
      setStaffOptions([]);
    }
  };

  const loadCustomersAndProducts = async () => {
    const [customersData, productsData] = await Promise.all([
      fetchCustomers(),
      fetchUniqueProducts(),
    ]);

    const customersMap = customersData?.items?.map(customerToOption);

    const productsMap = productsData.map((option) => ({
      value: option.product.product_code.trim(),
      label: option.label,
    }));

    setProductOptions(productsMap);
    setCustomerOptions(customersMap || []);

    return { customersMap: customersMap || [], productsMap };
  };

  const handleSearchCustomer = useCallback(async (search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!search.trim()) {
        const customersData = await fetchCustomers();
        const customersMap = customersData?.items?.map(customerToOption);
        setCustomerOptions(customersMap || []);
        return;
      }

      setCustomerLoading(true);
      try {
        const customersData = await searchCustomers(search);
        const customersMap = customersData?.items?.map(customerToOption);
        setCustomerOptions(customersMap || []);
      } catch (err) {
        console.error("Failed to search customers:", err);
      } finally {
        setCustomerLoading(false);
      }
    }, 300);
  }, []);

  const handleSearchProduct = useCallback(async (search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setProductLoading(true);
      try {
        if (!search.trim()) {
          const productsData = await fetchUniqueProducts();
          setProductOptions(
            productsData.map((option) => ({
              value: option.product.product_code,
              label: option.label,
            })),
          );
          return;
        }

        const productsData = await searchProducts(search.trim());
        setProductOptions(
          (productsData?.items || []).map((product: any) => ({
            value: product.product_code.trim(),
            label: `${product.product_code} - ${product.product_name}`,
          })),
        );
      } catch (err) {
        console.error("Failed to search products:", err);
      } finally {
        setProductLoading(false);
      }
    }, 300);
  }, []);

  // ===== ADD =====
  const handleAdd = () => {
    const newItem: OrderItem = {
      id: Date.now(),
      order_quantity: 1,
      order_type: currentOrderType,
    };

    if (isSingle && productSelected) {
      newItem.product_code = productSelected.value as string;
      newItem.product_name =
        productSelected.label?.split(" - ")[1] || productSelected.label;
    }

    setItems([...items, newItem]);
  };

  // ===== DELETE =====
  const handleDelete = (id: number | undefined) => {
    setItems(items.filter((i) => i.id !== id));
  };

  // ===== UPDATE =====
  const handleChange = (id: number | undefined, updated: OrderItem) => {
    const existing = items.find((item) => item.id === id);
    if (existing?.product_code !== updated.product_code) {
      setLeadTimeResults({});
    }
    setItems(items.map((i) => (i.id === id ? updated : i)));
  };

  // ===== TOTAL =====
  const total = items.reduce(
    (sum, i) => sum + (i.order_quantity || 0) * (i.proposed_price || 0),
    0,
  );
  const singleLeadTimeResult = getLeadTimeResult(
    leadTimeResults,
    productSelected?.value,
  );
  const singleLeadTimeError = buildLeadTimeErrorMessage(singleLeadTimeResult);
  const hasLeadTimeViolation = Object.values(leadTimeResults).some(
    (item) => item.is_valid === false,
  );

  const handleSave = (type: string) => async () => {
    const showWarning = (desc: string) => {
      appNotification.warning({
        message: "入力エラー",
        description: desc,
      });
    };

    if (isSingle && !productSelected) {
      showWarning("商品を選択してください");
      return;
    }

    if (items.length === 0) {
      showWarning("注文明細を追加してください");
      return;
    }

    if (!salesDate) {
      showWarning("出荷日を選択してください");
      return;
    }

    if (!customerSelected) {
      showWarning("お客様を選択してください");
      return;
    }

    if (leadTimeChecking) {
      showWarning("注文期限を確認中です。しばらくお待ちください");
      return;
    }

    if (hasLeadTimeViolation) {
      showWarning("出荷日が注文期限ルールを満たしていません");
      return;
    }

    const selectedStaff = staffOptions.find(
      (option) => option.value === selectedStaffId,
    );

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!isSingle && !item.product_code) {
        showWarning(`注文明細の${i + 1}行目の商品を選択してください`);
        return;
      }
      if (
        item.order_quantity === undefined ||
        item.order_quantity === null ||
        item.order_quantity <= 0
      ) {
        showWarning(`注文明細の${i + 1}行目の数量を入力してください`);
        return;
      }
    }

    const itemsForDuplicateValidation =
      isSingle && productSelected
        ? items.map((item) => ({
            ...item,
            product_code: String(productSelected.value),
            product_name:
              productSelected.label?.split(" - ")[1] || productSelected.label,
          }))
        : items;
    const duplicateRows = findDuplicateOrderItemRows(
      itemsForDuplicateValidation,
    );
    if (duplicateRows) {
      showWarning(
        `注文明細の${duplicateRows[0]}行目と${duplicateRows[1]}行目が完全に重複しています`,
      );
      return;
    }

    try {
      setLoading(true);

      const customerLabelParts =
        (customerSelected as any)?.label?.split(" - ") || [];

      const selectedLine = selectedStaff?.line;

      const payload: any = {
        type: currentOrderType,
        group_id: selectedLine?.group_id ?? null,
        customer_id: customerSelected?.value,
        customer_code: customerLabelParts[0]?.trim() || "",
        branch_code: customerLabelParts[1]?.trim() || "",
        line_id: selectedLine?.line_id || selectedLine?.line_code || null,
        status: type === "draft" ? "pending" : "confirmed",
        sales_date: salesDate?.format("YYYY-MM-DDTHH:mm:ss"),
        total_amount: total,
        notes:
          sourceOrder === "order" ? orderNotes.trim() || null : undefined,
        order_items: items.map((i, index) => {
          const pName = isSingle
            ? (productSelected?.label as string)?.split(" - ")[1] ||
              productSelected?.label
            : i.product_name;

          const linePayload = {
            ...i,
            line_number: index + 1,
            order_type: currentOrderType,
            product_code: (isSingle
              ? productSelected?.value
              : i.product_code) as string,
            product_name: pName,
            line_total: (i.order_quantity || 0) * (i.proposed_price || 0),
          };
          if (mode === "COPY") {
            delete linePayload.id;
          }
          delete linePayload.origin_id;
          delete linePayload.shipper_id;
          return linePayload;
        }),
      };

      if (!orderId || mode === "COPY") {
        await createOrder(payload);
        appNotification.success({
          message: mode === "COPY" ? "コピー完了" : "保存完了",
          description: "注文データが正常に保存されました。",
        });
      } else {
        await updateOrder(orderId, payload);
        appNotification.success({
          message: "更新完了",
          description: "注文データが正常に更新されました。",
        });
      }
      navigate("/orders");
    } catch (err: unknown) {
      const apiError = normalizeApiError(err);
      console.error(apiError);
      if (apiError.code === "ORDER_LEAD_TIME_VIOLATION") {
        const details = apiError.details as
          | {
              items?: Array<{
                product_code?: string;
                earliest_delivery_date?: string;
                lead_time_days?: number;
                cutoff_time?: string | null;
                cutoff_extra_days?: number;
              }>;
            }
          | undefined;
        const violationItems: OrderLeadTimeValidationItem[] =
          details?.items?.flatMap((item) =>
            item.product_code && item.earliest_delivery_date
              ? [
                  {
                    product_code: item.product_code,
                    rule_found: true,
                    is_valid: false,
                    earliest_delivery_date: item.earliest_delivery_date,
                    lead_time_days: item.lead_time_days ?? 0,
                    cutoff_time: item.cutoff_time ?? null,
                    cutoff_extra_days: item.cutoff_extra_days ?? 0,
                  },
                ]
              : [],
          ) || [];
        setLeadTimeResults(indexLeadTimeResults(violationItems));
      }
      const fieldDescription = Object.entries(apiError.fields || {})
        .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
        .join("\n");
      const description = fieldDescription || apiError.message;
      appNotification.error({
        message: "保存エラー",
        description,
        duration: 8,
      });
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  // ===== RENDER =====
  return (
    <div className="wrapper">
      <div className="container">
        <BasicInfoCard
          customer={customerSelected}
          product={productSelected}
          salesDate={salesDate}
          onChangeCustomer={(value) => {
            setLeadTimeResults({});
            setCustomerSeleted(value);
            setSelectedStaffId(undefined);
          }}
          onChangeProduct={(value) => {
            setLeadTimeResults({});
            setProductSeleted(value);
          }}
          onChangeDate={(value) => {
            setLeadTimeResults({});
            setSalesDate(value);
          }}
          companyName={companyData}
          customerOptions={customerOptions}
          productOptions={productOptions}
          staffId={selectedStaffId}
          onChangeStaff={(value) => {
            setSelectedStaffId(value);
          }}
          staffOptions={staffOptions}
          mode={mode}
          orderType={orderType}
          onSearchCustomer={handleSearchCustomer}
          customerLoading={customerLoading}
          onSearchProduct={handleSearchProduct}
          productLoading={productLoading}
          readOnly={isReadOnly}
          sourceOrder={sourceOrder}
          deliveryDateDisabled={
            isSingle && (!customerSelected || !productSelected)
          }
          minimumDeliveryDate={
            isSingle ? singleLeadTimeResult?.earliest_delivery_date : null
          }
          deliveryDateError={isSingle ? singleLeadTimeError : undefined}
        />

        {leadTimeCheckError && !isReadOnly && (
          <div className="lead-time-check-error">{leadTimeCheckError}</div>
        )}

        {(isSingle ? !!productSelected : true) && (
          <>
            {" "}
            {/* Use a Fragment here instead of a div if you don't need an extra DOM node */}
            <div className="section-header">
              <span>注文明細 ({items.length})</span>
              <span className="total">合計金額: {formatYen(total)}</span>
            </div>
            {/* LIST */}
            {items.map((item, index) => (
              <OrderItemCard
                key={item.id}
                value={item}
                itemNumber={index + 1}
                onChange={(val) => handleChange(item.id, val)}
                onDelete={() => handleDelete(item.id)}
                productCode={
                  isSingle ? productSelected?.value : item.product_code
                }
                productName={
                  isSingle
                    ? productSelected?.label?.split(" - ")[1]
                    : productOptions
                        .find((p) => p.value === item.product_code)
                        ?.label?.split(" - ")[1]
                }
                isSingle={isSingle}
                productOptions={productOptions}
                productLoading={productLoading}
                onSearchProduct={handleSearchProduct}
                readOnly={isReadOnly}
                sourceOrder={sourceOrder}
                leadTimeError={
                  !isSingle
                    ? buildLeadTimeErrorMessage(
                        getLeadTimeResult(leadTimeResults, item.product_code),
                      )
                    : undefined
                }
              />
            ))}
          </>
        )}

        {sourceOrder === "order" && (
          <div className="order-notes-field">
            <label htmlFor="order-notes">備考</label>
            <Input.TextArea
              id="order-notes"
              placeholder="注文全体に関する特記事項があれば入力..."
              value={orderNotes}
              onChange={(event) => setOrderNotes(event.target.value)}
              autoSize={{ minRows: 3, maxRows: 8 }}
              readOnly={isReadOnly}
            />
          </div>
        )}
      </div>

      {!isReadOnly && (!isSingle || !!productSelected) && (
        <button className="fab" onClick={handleAdd}>
          <PlusOutlined /> 新規行を追加
        </button>
      )}

      <div className="footer">
        {isReadOnly ? (
          <button className="draft" onClick={() => navigate(-1)}>
            戻る
          </button>
        ) : (
          <>
            <button
              className="draft"
              onClick={handleSave("draft")}
              disabled={loading || leadTimeChecking || hasLeadTimeViolation}
            >
              下書き保存
            </button>
            <button
              className="save"
              onClick={handleSave("confirmed")}
              disabled={loading || leadTimeChecking || hasLeadTimeViolation}
            >
              {loading ? "保存中..." : "最終確定する"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderEntrySM;
